import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import Select from 'react-select';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import ApiService from "utills/ApiService";
import toast from "react-hot-toast";
import DatePickerChart from "components/wps/DatePickerChart";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const ChartOverview = (props: any) => {
  const { translate, isMobile, setLoading, dateType, setDateType, dateValue, setDateValue, currentCurrency, showPlatform, token } = props;
  const [displayChart, setDisplayChart] = useState(true);
  const [dataChart, setDataChart] = useState(null as any);
  const [chartOptions, setChartOptions] = useState(null as any);
  const [chartSeries, setChartSeries] = useState(null as any);
  const [filter, setFilter] = useState(false);
  const ticket_platform_option = [
    {
      value: '',
      label: translate('all_platform')
    },
    {
      value: "actiup",
      label: translate('actiup_platform')
    },
    {
      value: "partner",
      label: translate('partner_platform')
    }
  ] as any;

  const [ticketPlatform, setTicketPlatform] = useState(ticket_platform_option[0].value);

  useEffect(() => {
    if (dateValue === '') {
      const year = moment().format('YYYY');

      setDateValue(year);

      setFilter(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateValue]);

  useEffect(() => {
    if (filter) {
      fetchDataChart();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchDataChart = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`dashboards/orders/charts?type=${dateType}&value=${dateValue}&currency=${currentCurrency}&ticket_platform=${ticketPlatform}`, token);

      if (result && Object.keys(result).length > 0) {
        setDisplayChart(true);
        setDataChart(result);
      } else {
        setDisplayChart(false);
      }
    } catch (error: any) {
      // toast.error(error.message);
      console.log("Fetch data chart failed:", error.message);
    }
    setFilter(false);
    setLoading(false);
  }

  useEffect(() => {
    if (dataChart) {
      handleDisplayChart();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataChart]);

  const handleDisplayChart = () => {
    let cates = [] as any;
    const net_revenue = [] as any;
    const gross_revenue = [] as any;
    const total_item = [] as any;
    const keys = Object.keys(dataChart);

    keys.forEach((item: any) => {
      let date = '';
      if (dateType === 'month') {
        const month = dateValue.slice(0, 2);
        date = (+item < 10) ? `0${item}/${month}` : `${item}/${month}`;
      } else if (dateType === 'quarter') {
        date = `Q${item}`;
      } else {
        date = item;
      }

      net_revenue.push(dataChart[item].grand_total);
      gross_revenue.push(dataChart[item].subtotal);
      total_item.push(dataChart[item].total_item);
      cates.push(date);
    });

    const options = {
      chart: {
        id: "multi-series-chart",
        toolbar: {
          show: false,
        },
        stacked: false,
      },
      xaxis: { categories: cates },
      yaxis: [
        {
          seriesName: translate('gross-revenue-event'),
          title: { 
            text: translate('chart-revenue'),
            style: {
              fontSize: '12px',
              fontFamily: 'Inter',
              fontWeight: 400,
              color: '#2B3033'
            }
          },
          labels: {
            formatter: function (value: any) {
              let last_value = '';
              if (value > 0) {
                if (value >= 1000000000000) {
                  let shortened = (value / 1000000000000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  last_value = shortened.endsWith('.0') ? shortened.slice(0, -2) + 'T' : shortened + 'T';
                } else if (value >= 1000000000) {
                  let shortened = (value / 1000000000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  last_value = shortened.endsWith('.0') ? shortened.slice(0, -2) + 'B' : shortened + 'B';
                } else if (value >= 1000000) {
                  let shortened = (value / 1000000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  last_value = shortened.endsWith('.0') ? shortened.slice(0, -2) + 'M' : shortened + 'M';
                } else if (value >= 1000) {
                  let shortened = (value / 1000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  last_value = shortened.endsWith('.0') ? shortened.slice(0, -2) + 'K' : shortened + 'K';
                } else {
                  last_value = value;
                }

                return `${(currentCurrency && currentCurrency === 'usd') ? '$' : 'đ'} ${last_value}`;
              } else {
                return 0;
              }
            },
          },
        },
        {
          seriesName: translate('gross-revenue-event'),
          title: { text: translate('chart-revenue') },
          labels: {
            formatter: function (value: any) {
              let last_value = '';
              if (value > 0) {
                if (value >= 1000000000000) {
                  let shortened = (value / 1000000000000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  last_value = shortened.endsWith('.0') ? shortened.slice(0, -2) + 'T' : shortened + 'T';
                } else if (value >= 1000000000) {
                  let shortened = (value / 1000000000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  last_value = shortened.endsWith('.0') ? shortened.slice(0, -2) + 'B' : shortened + 'B';
                } else if (value >= 1000000) {
                  let shortened = (value / 1000000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  last_value = shortened.endsWith('.0') ? shortened.slice(0, -2) + 'M' : shortened + 'M';
                } else if (value >= 1000) {
                  let shortened = (value / 1000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  last_value = shortened.endsWith('.0') ? shortened.slice(0, -2) + 'K' : shortened + 'K';
                } else {
                  last_value = value;
                }

                return `${(currentCurrency && currentCurrency === 'usd') ? '$' : 'đ'} ${last_value}`;
              } else {
                return 0;
              }
            },
          },
        },
        {
          seriesName: translate('chart-ticket'),
          opposite: true,
          title: {
            text: translate('chart-ticket'),
            style: {
              fontSize: '12px',
              fontFamily: 'Inter',
              fontWeight: 400,
              color: '#2B3033'
            }
          },
          labels: {
            formatter: function (value: any) {
              if (value > 0) {
                if (value >= 1000000000000) {
                  let shortened = (value / 1000000000000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  return shortened.endsWith('.0') ? shortened.slice(0, -2) + 'T' : shortened + 'T';
                } else if (value >= 1000000000) {
                  let shortened = (value / 1000000000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  return shortened.endsWith('.0') ? shortened.slice(0, -2) + 'B' : shortened + 'B';
                } else if (value >= 1000000) {
                  let shortened = (value / 1000000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  return shortened.endsWith('.0') ? shortened.slice(0, -2) + 'M' : shortened + 'M';
                } else if (value >= 1000) {
                  let shortened = (value / 1000) as any;
                  shortened = (Math.floor(shortened * 10) / 10).toFixed(1);
                  return shortened.endsWith('.0') ? shortened.slice(0, -2) + 'K' : shortened + 'K';
                } else {
                  return value;
                }
              } else {
                return 0;
              }
            }
          }
        },
      ],
      colors: ["#1B9DA0", "#EEAE0E", "#8044FF"],
      tooltip: {
        y: {
          formatter: (value: any, series: any) => {
            if (series.seriesIndex < 2) {
              let convertValue = (currentCurrency && currentCurrency === 'usd') ? value.toFixed(2) : value.toLocaleString();
              return `${(currentCurrency && currentCurrency === 'usd') ? '$' : 'đ'} ${convertValue.toLocaleString()}`;
            } else {
              return `${value.toLocaleString()}`;
            }
          },
        },
      },
    };

    const series = [
      { name: translate('gross-revenue-event'), data: gross_revenue, type: 'line', yAxisIndex: 0 },
      { name: translate('net-revenue-event'), data: net_revenue, type: 'line', yAxisIndex: 0 },
      { name: translate('chart-ticket'), data: total_item, type: 'line', yAxisIndex: 1 },
    ];

    setChartOptions(options);

    setChartSeries(series);
  }

  return (
    <div className={`w-full bg-white ${isMobile ? 'my-[16px]' : 'rounded-[8px] my-[8px]'}`}>
      <div className={`${isMobile ? 'w-full' : 'h-[80px] px-[22px] flex items-center justify-between border-b border-[#c5ccd2]'}w-full`}>
        <div className={`${isMobile ? 'w-full p-[16px] flex items-center gap-x-[4px] border-b border-[#c5ccd2]' : 'text-16 font-700 flex items-center gap-x-[4px]'}`}>{translate("sale-tickets")}
          <LightTooltip title={translate("tooltip-chart")} className="w-[200px]" placement="top-start">
            <img src="/images/icons/tooltip.svg" alt="icon-tooltip" className="icon-tooltip" />
          </LightTooltip>
        </div>

        <div className={`${isMobile ? 'w-full grid grid-cols-1 gap-[12px] p-[16px]' : 'flex items-center justify-center gap-x-[12px]'}`}>
          {
            showPlatform &&
            <div className="filters-ticket-platform">
              {
                !isMobile &&
                <div className="text-14 font-500 mb-[4px]">{translate('ticket_platform')}</div>
              }

              <Select
                styles={{
                  input: (base) => ({
                    ...base,
                    "input:focus": {
                      boxShadow: "none",
                    },
                  })
                }}
                name="ticket_platform"
                options={ticket_platform_option}
                className="filter-item__select"
                classNamePrefix="select"
                placeholder={ticket_platform_option[0].label}
                onChange={(e: any) => {
                  setTicketPlatform(e.value);
                  setFilter(true);
                }} />

            </div>
          }

          <div className={`${isMobile ? 'w-full' : 'w-[435px]'}`}>
            {
              !isMobile &&
              <div className="text-14 font-500 mb-[4px]">{translate('time')}</div>
            }

            <div className="date-picker-wps">
              <DatePickerChart
                isMobile={isMobile}
                setDateType={setDateType}
                setDateValue={setDateValue}
                dateType={dateType}
                dateValue={dateValue}
                setFilter={setFilter}
              />
            </div>
          </div>
        </div>

      </div>

      <div className="chart-body">
        {
          displayChart &&
          <div id="chart">
            {
              (chartOptions && chartSeries) &&
              <Chart options={chartOptions} series={chartSeries} type="line" height={500} />
            }
          </div>
        }

      </div>
    </div>
  )
}

export default ChartOverview;